import { z } from 'zod';
/**
 * Contrats du NETTOYAGE DES DOSSIERS VIDES (outil « Nettoyer les dossiers vides »).
 * Source unique de vérité front + back.
 *
 * POURQUOI CET OUTIL : un dossier n'est PAS un objet d'xphoto — la liste des dossiers
 * est DÉRIVÉE du chemin des assets (`assets.dir_path`). Un dossier dont on a sorti (ou
 * supprimé) toutes les photos disparaît donc TOUT SEUL de l'UI, mais le RÉPERTOIRE
 * physique, lui, restait sur disque à jamais : rien n'appelait `rmdir` (le job `move`
 * fait `mkdir -p` à destination sans nettoyer la source ; la purge de corbeille se limite
 * à `unlink`). Cet outil est la seule reprise de ces répertoires devenus orphelins.
 *
 * - `GET  /api/libraries/:id/empty-folders`       → `EmptyFoldersScanResult` (aperçu).
 * - `POST /api/libraries/:id/empty-folders/clean` → enfile un job `empty_folders_cleanup`.
 *
 * GARANTIE DE SÛRETÉ (cf. ADR-0010) : le job n'appelle QUE `rmdir`, JAMAIS `unlink` — il
 * ne peut pas effacer un fichier, même par bug, et `rmdir` échoue de lui-même sur un
 * répertoire non vide. Les répertoires CACHÉS (`.` en tête) sont sanctuarisés : un
 * `.stfolder` (Syncthing) vide est un MARQUEUR, pas un déchet.
 */
/** Longueur max de l'échantillon de dossiers renvoyé par l'aperçu (garde-fou UI). */
export const EMPTY_FOLDERS_SAMPLE_MAX = 100;
/**
 * LISTE BLANCHE des fichiers « système » — ceux qu'un système d'exploitation sème sans
 * qu'on le lui demande, et qui empêchent seuls un dossier d'être vide.
 *
 * Ce sont des **noms EXACTS**, comparés en minuscules (Windows et macOS ignorent la
 * casse), JAMAIS des motifs. C'est la condition qui rend l'amendement d'ADR-0010
 * acceptable : le seul `unlink` du produit ne peut atteindre que ces quatre noms, et
 * l'utilisateur doit encore le demander explicitement (`removeSystemFiles`).
 *
 *  - `.DS_Store`   : index de dossier macOS (Finder) ;
 *  - `._.DS_Store` : sa doublure AppleDouble, semée sur les volumes non-HFS ;
 *  - `Thumbs.db`   : cache de vignettes de l'Explorateur Windows ;
 *  - `desktop.ini` : réglages d'affichage de dossier Windows.
 *
 * NE FIGURENT PAS ICI, délibérément : `@eaDir` (Synology) et `.stversions` (Syncthing)
 * sont des RÉPERTOIRES peuplés — les supprimer supposerait un effacement récursif, une
 * tout autre classe de risque ; les motifs AppleDouble `._<nom>` sont écartés parce
 * qu'ils accompagnent un vrai fichier, dont ils portent parfois les métadonnées.
 */
export const SYSTEM_FILENAMES = ['.DS_Store', '._.DS_Store', 'Thumbs.db', 'desktop.ini'];
/** Index minuscule de `SYSTEM_FILENAMES` (comparaison insensible à la casse). */
const SYSTEM_FILENAMES_LOWER = new Set(SYSTEM_FILENAMES.map((n) => n.toLowerCase()));
/**
 * Ce nom de fichier est-il un fichier système de la liste blanche ? PURE, partagée
 * front + back : le serveur s'en sert pour classer et pour effacer, la SPA pour dire à
 * l'utilisateur exactement ce qui sera touché.
 */
export function isSystemFilename(name) {
    return SYSTEM_FILENAMES_LOWER.has(name.toLowerCase());
}
/**
 * Aperçu (LECTURE), en DEUX catégories disjointes, chacune avec son total et un
 * échantillon borné de chemins RELATIFS POSIX (ordre de SUPPRESSION, enfants avant
 * parents : `2024/vacances/raw` précède `2024/vacances`) :
 *
 *  - `total`/`sample` : dossiers RÉELLEMENT vides — supprimables sans rien effacer ;
 *  - `systemOnlyTotal`/`systemOnlySample` : dossiers dont le seul contenu est des
 *    fichiers de `SYSTEM_FILENAMES` (et/ou des sous-dossiers de même nature). Ils ne
 *    sont JAMAIS supprimés sans `removeSystemFiles` — ils sont montrés pour que
 *    l'utilisateur sache qu'ils existent, au lieu de se demander pourquoi un dossier
 *    « vide » à l'œil ne figure pas dans la première liste.
 */
export const emptyFoldersScanResultSchema = z.object({
    total: z.number().int().nonnegative(),
    sample: z.array(z.string()),
    systemOnlyTotal: z.number().int().nonnegative(),
    systemOnlySample: z.array(z.string()),
});
/**
 * Corps de `POST /api/libraries/:id/empty-folders/clean` : enfile un job
 * `empty_folders_cleanup`. `dryRun` = compte sans rien supprimer.
 *
 * `removeSystemFiles` (défaut **false**) étend la cible aux dossiers dont le seul
 * contenu est des fichiers de `SYSTEM_FILENAMES` : ces fichiers sont alors effacés
 * (`unlink`, liste blanche STRICTE) avant le `rmdir` du dossier. C'est le SEUL chemin
 * du produit où un fichier est supprimé, et il est opt-in.
 *
 * Aucun autre sélecteur : la cible est TOUJOURS toute la bibliothèque, et elle est
 * RE-CALCULÉE par le job au moment de s'exécuter (jamais héritée de l'aperçu, périmable).
 */
export const cleanEmptyFoldersRequestSchema = z.object({
    dryRun: z.boolean().default(false),
    removeSystemFiles: z.boolean().default(false),
});
/**
 * Ventilation d'un job `empty_folders_cleanup`. Chaque dossier ciblé tombe dans
 * exactement une catégorie (sauf `total`, qui est le nombre de dossiers ciblés) :
 *  - `removed` : répertoire supprimé (ou, en dry-run, qui SERAIT supprimé).
 *  - `skipped` : plus vide (`ENOTEMPTY`) ou déjà disparu (`ENOENT`) au moment du
 *    `rmdir` — la bibliothèque a bougé entre le parcours et la suppression. Sans faute.
 *  - `failed`  : chemin refusé (traversée) ou erreur d'E/S disque.
 *
 * `filesRemoved` compte À PART les fichiers système effacés (`removeSystemFiles`) : ce
 * n'est pas une catégorie de dossier, et c'est le chiffre qu'on veut voir en évidence
 * quand on a autorisé le seul `unlink` du produit. Vaut 0 sans l'option.
 */
export const emptyFoldersResultSchema = z.object({
    total: z.number().int().nonnegative(),
    removed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    filesRemoved: z.number().int().nonnegative(),
});
//# sourceMappingURL=empty-folders.js.map